/* eslint-disable no-prototype-builtins */
import React, { useState } from 'react';
import { AxiosError } from 'axios';
import { Typography } from '@one-thd/sui-atomic-components';
import {
  useBusinessToolsContext,
  useCommonContext,
} from '../../hooks/useContexts';
import { SectionHeader } from '../shared/section-header/SectionHeader';
import { BusinessToolsErrorMessage } from './BuisnessToolsErrorMessage';
import { GetTopSpendByDepartment } from '../../hooks/business-tools-hooks';
import { spendPercentage } from '../../utils/business-tools';
import { DateRanges } from '../../utils/DateRanges';
import {
  IBusinessToolsRequest,
  IContextData,
  IDeptsSpend,
  ITopSpendByDepartmentResponse,
} from '../../models/business-tools';
import { getSvocId } from '../../utils/user';

import '../../styles/business-tools.scss';
import { SpendByDepartmentSkeleton } from './BusinessToolsSkeletons';

interface ISpendByDeptState {
  showMoreDepartments: number;
  setShowMoreDepartments: (value: number) => void;
  hideMe: boolean;
  setHideMe: (value: boolean) => void;
}

const colorKeyValuePair: { [key: number]: string } = {
  0: '#F96302',
  1: '#ff8364',
  2: '#fdb87d',
  3: '#d7bfab',
  4: '#ffe8d5',
};

const getBarWidth = (percentageSpent: number): string => {
  if (percentageSpent <= 0.5) {
    return percentageSpent > 0 ? '0.5%' : '0%';
  }
  return `${percentageSpent}%`;
};

// map over array and return html and values
const spendByDepartmentList = (
  spendByDepartmentData: IDeptsSpend[],
  showMoreDepartments: number,
  redirectQueryParams: IContextData,
) => {
  const denominatorAmount = spendByDepartmentData[0];
  return spendByDepartmentData.map((data: IDeptsSpend, i: number) => {
    const percentageSpent = spendPercentage(
      denominatorAmount.totalSpend,
      data.totalSpend,
    );

    if (showMoreDepartments === 0 && i > 4) {
      return true;
    }

    if (data.hasOwnProperty('totalSpend')) {
      /**
       * This is the data for linking to other page (purchase history) with search params
       */

      if (redirectQueryParams?.poJobName !== 'all') {
        Object.assign(redirectQueryParams, {
          searchBy: [
            {
              departmentNumber: data.deptNumber,
              POJobName: redirectQueryParams?.poJobName,
            },
          ],
        });
      }
      const operator = data.totalSpend < 0 ? '-' : '';
      return (
        <li className="sui-list-none sui-my-3" key={i}>
          <div
            // TODO: Change to button once CPS can redirect with proper data. No ETA on this
            // type="button"
            // onClick={() => {
            //   if (getWindow()) {
            //     const location = getWindow()?.location;
            //     location?.assign(redirectUrl);
            //   }
            // }}
            className="sui-block"
            style={{ width: '100%' }}
          >
            <div className="business-tools__Labels u__flex">
              <span>
                {data.deptName !== ''
                  ? data.deptName
                  : 'Department name unavailable'}
              </span>
              <span>{`${operator}$${Math.abs(data.totalSpend).toLocaleString(
                undefined,
                {
                  minimumFractionDigits: 2,
                },
              )}`}</span>
            </div>
            <div>
              <div
                style={{
                  backgroundColor: colorKeyValuePair[i]
                    ? colorKeyValuePair[i]
                    : '"#cccccc"',
                  width: getBarWidth(percentageSpent),
                  height: 15,
                }}
              />
            </div>
          </div>
        </li>
      );
    }
    return <></>;
  });
};

const renderSpendByDepartmentElement = (
  jobDepartmentData: ITopSpendByDepartmentResponse | null,
  jobDepartmentError: AxiosError | null,
  jobDepartmentLoaded: boolean,
  redirectQueryParams: IContextData,
  spendByDeptState: ISpendByDeptState,
  businessToolsHostName: string,
) => {
  if (jobDepartmentLoaded) {
    if (jobDepartmentError) {
      return BusinessToolsErrorMessage();
    }
    if (
      jobDepartmentData &&
      jobDepartmentData.businessToolsResponse.deptsSpend.length > 0
    ) {
      return (
        <>
          {' '}
          <div className="business-tools__fullWidth u__flex business-tools__subHeader business-tools__marginTop">
            <h4>Highest to Lowest</h4>
          </div>
          <div className="jobs u__clearfix">
            <div className="sui-grid sui-grid-cols-1 sui-gap-2">
              <ul className="department-list" style={{ width: '100%' }}>
                {spendByDepartmentList(
                  jobDepartmentData.businessToolsResponse.deptsSpend,
                  spendByDeptState.showMoreDepartments,
                  redirectQueryParams,
                )}
              </ul>
              {jobDepartmentData?.businessToolsResponse?.deptsSpend?.length >
              5 ? (
                <div className="business-tools__rmPaddingLeft">
                  {spendByDeptState.hideMe ? (
                    <button
                      type="button"
                      className="bttn-outline bttn--link"
                      id="showMoreDepartmentsElement"
                      onClick={() => {
                        spendByDeptState.setShowMoreDepartments(1);
                        spendByDeptState.setHideMe(false);
                      }}
                    >
                      <span className="bttn__content">
                        Show more departments
                      </span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="bttn-outline bttn--link"
                      id="showLessDepartmentsElement"
                      onClick={() => {
                        spendByDeptState.setShowMoreDepartments(0);
                        spendByDeptState.setHideMe(true);
                      }}
                    >
                      <span className="bttn__content">Show less</span>
                    </button>
                  )}
                </div>
              ) : (
                ''
              )}
            </div>
          </div>
        </>
      );
    }
    return (
      <Typography weight="bold">
        No records can be found for the selected date range and/or job name.
      </Typography>
    );
  }
  return <SpendByDepartmentSkeleton />;
};

export const SpendByDepartment = () => {
  const { dateRangeOptions, timezone } = useCommonContext();
  const { dateSelectorValue, requestData, businessToolsHostName } =
    useBusinessToolsContext();
  const [showMoreDepartments, setShowMoreDepartments] = useState(0);
  const [hideMe, setHideMe] = useState(true);

  const request: IBusinessToolsRequest = {
    businessToolsRequest: {
      startDate:
        dateSelectorValue === 'custom'
          ? dateRangeOptions.startDate
          : DateRanges[dateSelectorValue].startDate,
      endDate:
        dateSelectorValue === 'custom'
          ? dateRangeOptions.endDate
          : DateRanges[dateSelectorValue].endDate,
      jobName: requestData.poJobName,
      timezone,
    },
    customerAccountId: getSvocId() || '',
  };

  const redirectQueryParams: IContextData = {
    startDate:
      dateSelectorValue === 'custom'
        ? dateRangeOptions.startDate
        : DateRanges[dateSelectorValue].startDate,
    endDate:
      dateSelectorValue === 'custom'
        ? dateRangeOptions.endDate
        : DateRanges[dateSelectorValue].endDate,
    dateSelectorValue: dateSelectorValue || 'YTD',
    poJobName: requestData.poJobName || '',
  };

  const spendByDeptState: ISpendByDeptState = {
    showMoreDepartments,
    setShowMoreDepartments,
    hideMe,
    setHideMe,
  };

  const { jobDepartmentData, jobDepartmentLoaded, jobDepartmentError } =
    GetTopSpendByDepartment(request);

  return (
    <div className="spend-department">
      <SectionHeader title="Spend By Department" />
      {renderSpendByDepartmentElement(
        jobDepartmentData,
        jobDepartmentError,
        jobDepartmentLoaded,
        redirectQueryParams,
        spendByDeptState,
        businessToolsHostName,
      )}
    </div>
  );
};
